<template>
  <div class="min-h-screen flex flex-col bg-white py-0 px-0 relative">
    <!-- 品牌标志放在左上角 -->
    <div class="absolute top-6 left-6">
      <img src="/404.png" alt="Logo" class="h-10" />
    </div>
    
    <!-- 中央内容区域 -->
    <div class="flex-1 flex items-center justify-center">
      <div class="w-full max-w-md px-4">
        <!-- 欢迎标题 -->
        <div class="text-center mb-8">
          <h2 class="text-2xl font-semibold text-gray-900">欢迎使用</h2>
          <h1 class="text-3xl font-bold text-green-600 mt-1">游戏管理系统</h1>
        </div>
        
        <!-- 登录表单 -->
        <div class="bg-white border border-gray-200 rounded-lg shadow-sm px-8 pt-6 pb-8 mb-4">
          <form @submit.prevent="handleLogin">
            <!-- 错误信息显示 -->
            <div v-if="errorMessage" class="mb-4 bg-red-50 border-l-4 border-red-500 p-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm text-red-700">{{ errorMessage }}</p>
                </div>
              </div>
            </div>
            
            <!-- 用户名 -->
            <div class="mb-5">
              <label for="username" class="block text-sm font-medium text-gray-700 mb-1">
                用户名<span class="text-red-500">*</span>
              </label>
              <input
                id="username"
                type="text"
                v-model="form.username"
                required
                class="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 text-sm"
                placeholder="请输入用户名"
              />
            </div>
            
            <!-- 密码 -->
            <div class="mb-5">
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
                密码<span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <input
                  id="password"
                  :type="showPassword ? 'text' : 'password'"
                  v-model="form.password"
                  required
                  class="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 text-sm"
                  placeholder="请输入密码"
                />
                <button 
                  type="button" 
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg v-if="showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                  </svg>
                  <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- 记住我 -->
            <div class="flex items-center mb-6">
              <input
                id="remember-me"
                type="checkbox"
                v-model="form.rememberMe"
                class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label for="remember-me" class="ml-2 block text-sm text-gray-700">
                记住我
              </label>
            </div>
            
            <!-- 登录按钮 -->
            <div>
              <button
                type="submit"
                class="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                :disabled="loading"
              >
                <span v-if="loading" class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  登录中...
                </span>
                <span v-else>登录</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import AdminApiClient from '../utils/admin-api-client';

const apiClient = new AdminApiClient();
const router = useRouter();
const loading = ref(false);
const showPassword = ref(false);
const errorMessage = ref('');

const form = reactive({
  username: '',
  password: '',
  rememberMe: false
});

async function handleLogin() {
  errorMessage.value = '';
  loading.value = true;
  try {
    // 使用封装的登录方法
    const response = await apiClient.login({
      username: form.username,
      password: form.password,
      remember: form.rememberMe
    });
    
    // 处理响应
    if (response.success) {
      // 登录成功后跳转到仪表盘
      router.push('/admin/dashboard');
    } else {
      errorMessage.value = response.message || '登录失败';
    }
  } catch (error) {
    console.error('登录失败:', error);
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage.value = error.response.data.message;
    } else {
      errorMessage.value = '登录失败，请稍后再试';
    }
  } finally {
    loading.value = false;
  }
}

// 检查是否已登录
onMounted(() => {
  if (apiClient.isLoggedIn()) {
    // 如果已经登录，直接跳转到仪表盘
    router.push('/admin/dashboard');
  }
});
</script>

<style scoped>
/* 调整背景和边框颜色以准确匹配M-Pesa风格 */
:deep(.text-green-600) {
  color: #31A745;
}

:deep(.bg-green-600) {
  background-color: #31A745;
}

:deep(.hover\:bg-green-700:hover) {
  background-color: #218838;
}

:deep(.focus\:ring-green-500:focus) {
  --tw-ring-color: #31A745;
}

:deep(.focus\:border-green-500:focus) {
  border-color: #31A745;
}
</style> 